import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Product, ProductMeasurementRow } from '@rosita/shared';
import { api } from '../../lib/api';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { formatEur, formatWeight } from '../../lib/format';
import { useCartStore } from '../../stores/cartStore';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Accordion } from '../../components/ui/Accordion';
import { ProductCard } from '../../components/ui/ProductCard';
import { Lightbox } from '../../components/ui/Lightbox';
import {
  formatCm,
  formatComposition,
  getProductBadges,
  measurementLetter,
  sortSizes,
} from '../../lib/product';
import { useAppSettings } from '../../lib/appSettings';
import { useCartConfirmation } from '../../components/cart/CartConfirmation';
import styles from './ProductDetailPage.module.scss';

interface ProductListResponse {
  items: Product[];
}

function DetailsBlock({ product }: { product: Product }): JSX.Element {
  const weight = formatWeight(product.weightGrams);
  const composition = product.composition ?? [];

  if (!weight && composition.length === 0) {
    return <span className={styles.detailsEmpty}>—</span>;
  }

  return (
    <dl className={styles.detailsList}>
      {weight ? (
        <div className={styles.detailsRow}>
          <dt>{t.product.detailsWeight}</dt>
          <dd>{weight}</dd>
        </div>
      ) : null}
      {composition.length > 0 ? (
        <div className={styles.detailsRow}>
          <dt>{t.product.detailsComposition}</dt>
          <dd>{formatComposition(composition)}</dd>
        </div>
      ) : null}
    </dl>
  );
}

/** Measurement rows that have at least one filled-in size. */
function visibleMeasurementRows(product: Product): ProductMeasurementRow[] {
  return (product.measurements ?? []).filter((row) =>
    Object.values(row.values).some((v) => v != null)
  );
}

const MEASUREMENTS_ACCORDION_ID = 'medidas';

function openMeasurements(): void {
  const el = document.getElementById(MEASUREMENTS_ACCORDION_ID);
  if (el instanceof HTMLDetailsElement) {
    el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function MeasurementsBlock({
  rows,
  selectedSize,
  note,
}: {
  rows: ProductMeasurementRow[];
  selectedSize: string;
  note: string | null | undefined;
}): JSX.Element {
  const sizeSet = new Set<string>();
  for (const row of rows) {
    for (const [size, value] of Object.entries(row.values)) {
      if (value != null) sizeSet.add(size);
    }
  }
  const sizes = sortSizes(Array.from(sizeSet));

  return (
    <div className={styles.measures}>
      <div className={styles.measuresScroll}>
        <table className={styles.measuresTable}>
          <thead>
            <tr>
              <th scope="col">{t.product.measurementsPoint}</th>
              {sizes.map((s) => (
                <th
                  key={s}
                  scope="col"
                  className={s === selectedSize ? styles.colSelected : undefined}
                  aria-current={s === selectedSize ? 'true' : undefined}
                >
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.point.id}>
                <th scope="row">
                  <span className={styles.measureLetter} aria-hidden>
                    {measurementLetter(i)}
                  </span>
                  {row.point.name}
                </th>
                {sizes.map((s) => {
                  const v = row.values[s];
                  return (
                    <td key={s} className={s === selectedSize ? styles.colSelected : undefined}>
                      {v == null ? '—' : formatCm(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={styles.measuresHint}>{t.product.measurementsHint}</p>
      {note ? <p className={styles.measuresNote}>{note}</p> : null}
    </div>
  );
}

export default function ProductDetailPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedColour, setSelectedColour] = useState<string>('');
  const [showSelectHint, setShowSelectHint] = useState(false);
  const [related, setRelated] = useState<Product[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const measurementRows = useMemo(
    () => (product ? visibleMeasurementRows(product) : []),
    [product]
  );

  const addItem = useCartStore((s) => s.addItem);
  const cartConfirmation = useCartConfirmation();
  const { newBadgeExpiryDays } = useAppSettings();

  useEffect(() => {
    setLoading(true);
    setSelectedSize('');
    setSelectedColour('');
    setShowSelectHint(false);
    void (async () => {
      try {
        const response = await api.get<Product>(`/products/${slug}`);
        setProduct(response.data);
      } catch {
        navigate('/404', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  useEffect(() => {
    if (!product) return;
    const categorySlug = product.category?.slug;
    if (!categorySlug) {
      setRelated([]);
      return;
    }
    void (async () => {
      try {
        const res = await api.get<ProductListResponse>(
          `/products?category=${encodeURIComponent(categorySlug)}&limit=8`,
        );
        setRelated(res.data.items.filter((p) => p.slug !== product.slug).slice(0, 6));
      } catch {
        setRelated([]);
      }
    })();
  }, [product]);

  const availableSizes = useMemo(() => {
    if (!product) return [] as string[];
    return Array.from(new Set(product.variants.map((v) => v.size)));
  }, [product]);

  const availableColours = useMemo(() => {
    if (!product) return [] as { colour: string; hex: string | null }[];
    const map = new Map<string, string | null>();
    product.variants.forEach((v) => {
      if (!map.has(v.colour)) map.set(v.colour, v.colourHex);
    });
    return Array.from(map.entries()).map(([colour, hex]) => ({ colour, hex }));
  }, [product]);

  const selectedVariant = useMemo(() => {
    if (!product || !selectedSize || !selectedColour) return null;
    return (
      product.variants.find(
        (v) => v.size === selectedSize && v.colour === selectedColour,
      ) ?? null
    );
  }, [product, selectedSize, selectedColour]);

  const isSizeUnavailable = (size: string): boolean => {
    if (!product) return false;
    const pool = selectedColour
      ? product.variants.filter((v) => v.colour === selectedColour)
      : product.variants;
    const rows = pool.filter((v) => v.size === size);
    if (rows.length === 0) return true;
    return rows.every((v) => v.stock === 0);
  };

  const isColourUnavailable = (colour: string): boolean => {
    if (!product) return false;
    const pool = selectedSize
      ? product.variants.filter((v) => v.size === selectedSize)
      : product.variants;
    const rows = pool.filter((v) => v.colour === colour);
    if (rows.length === 0) return true;
    return rows.every((v) => v.stock === 0);
  };

  const handleSelectColour = (colour: string): void => {
    if (!product) return;
    setSelectedColour(colour);
    setShowSelectHint(false);
    if (selectedSize) {
      const rows = product.variants.filter(
        (v) => v.colour === colour && v.size === selectedSize,
      );
      if (rows.length === 0 || rows.every((v) => v.stock === 0)) {
        setSelectedSize('');
      }
    }
  };

  const handleSelectSize = (size: string): void => {
    if (!product) return;
    setSelectedSize(size);
    setShowSelectHint(false);
    if (selectedColour) {
      const rows = product.variants.filter(
        (v) => v.size === size && v.colour === selectedColour,
      );
      if (rows.length === 0 || rows.every((v) => v.stock === 0)) {
        setSelectedColour('');
      }
    }
  };

  if (loading) {
    return (
      <div className={`container ${styles.loading}`}>{t.common.loading}</div>
    );
  }
  if (!product) {
    return (
      <div className={`container ${styles.loading}`}>Produto não encontrado.</div>
    );
  }

  const badges = getProductBadges(product, newBadgeExpiryDays);
  const price = product.salePrice ?? product.price;
  const primaryImage = product.images[0];
  const galleryImages = [0, 1, 2, 3].map((i) => product.images[i] ?? null);

  const handleAdd = (e: MouseEvent<HTMLButtonElement>): void => {
    if (!selectedVariant) {
      setShowSelectHint(true);
      return;
    }
    const unitPrice =
      Number(product.salePrice ?? product.price) + Number(selectedVariant.priceModifier);
    const cartItem = {
      productId: product.id,
      variantId: selectedVariant.id,
      slug: product.slug,
      name: product.name,
      size: selectedVariant.size,
      colour: selectedVariant.colour,
      price: unitPrice,
      quantity: 1,
      imageUrl: primaryImage?.url ?? null,
      maxStock: selectedVariant.stock,
    };
    addItem(cartItem);
    cartConfirmation.show({ item: cartItem, triggerEl: e.currentTarget });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.shortDescription ?? product.description ?? '',
    image: product.images.map((i) => i.url),
    sku: product.sku ?? undefined,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'EUR',
      price,
      availability: product.variants.some((v) => v.stock > 0)
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
    },
  };

  const isLowStock =
    selectedVariant && selectedVariant.stock > 0 && selectedVariant.stock < 5;
  const addDisabled = !selectedVariant || selectedVariant.stock < 1;

  return (
    <>
      <SEO
        title={product.metaTitle ?? product.name}
        description={product.metaDescription ?? product.shortDescription ?? undefined}
        ogImage={primaryImage?.url}
        structuredData={structuredData}
      />

      <div className="container">
        <nav className={styles.breadcrumb} aria-label={t.common.breadcrumb}>
          <Link to="/">{t.products.breadcrumbHome}</Link>
          <span aria-hidden> / </span>
          <Link to="/produtos">{t.products.breadcrumbShop}</Link>
          <span aria-hidden> / </span>
          <span>{product.name}</span>
        </nav>
      </div>

      <div className={`container ${styles.wrapper}`}>
        <div className={styles.gallery}>
          {galleryImages.map((img, i) => {
            const cellClass = `${styles.cell} ${badges.isOutOfStock ? styles.cellOut : ''} ${img ? styles.cellClickable : ''}`;
            return (
              <button
                key={i}
                type="button"
                className={cellClass}
                disabled={!img}
                aria-label={
                  img ? `Ampliar imagem ${i + 1} de ${product.images.length}` : undefined
                }
                onClick={img ? () => setLightboxIndex(i) : undefined}
              >
                {img ? (
                  <img src={img.url} alt={img.altText ?? product.name} />
                ) : (
                  <div className={styles.cellPlaceholder} aria-hidden />
                )}
                {i === 0 && badges.isNew ? (
                  <div className={styles.cellBadgeTop}>
                    <Badge variant="new" />
                  </div>
                ) : null}
                {i === 0 && badges.isOutOfStock ? (
                  <div className={styles.cellBadgeCenter}>
                    <Badge variant="out" />
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className={styles.info}>
          <h1 className={styles.title}>{product.name}</h1>

          <p className={styles.price}>
            {product.salePrice ? (
              <>
                <span className={styles.sale}>{formatEur(product.salePrice)}</span>
                <span className={styles.original}>{formatEur(product.price)}</span>
              </>
            ) : (
              <span>{formatEur(product.price)}</span>
            )}
          </p>

          {product.shortDescription ? (
            <p className={styles.short}>{product.shortDescription}</p>
          ) : null}

          <div className={styles.field} role="group" aria-labelledby="colour-label">
            <span id="colour-label" className={styles.fieldLabel}>
              {t.product.colour}
              {selectedColour ? `: ${selectedColour}` : ''}
            </span>
            <div className={styles.swatchRow}>
              {availableColours.map((c) => {
                const disabled = isColourUnavailable(c.colour);
                const selected = c.colour === selectedColour;
                const cls = [
                  styles.swatch,
                  selected ? styles.swatchSelected : '',
                  disabled ? styles.swatchDisabled : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={c.colour}
                    type="button"
                    className={cls}
                    style={c.hex ? { background: c.hex } : undefined}
                    aria-label={c.colour}
                    aria-pressed={selected}
                    aria-disabled={disabled || undefined}
                    tabIndex={disabled ? -1 : 0}
                    title={c.colour}
                    onClick={disabled ? undefined : () => handleSelectColour(c.colour)}
                  />
                );
              })}
            </div>
          </div>

          <div className={styles.field} role="group" aria-labelledby="size-label">
            <div className={styles.fieldLabelRow}>
              <span id="size-label" className={styles.fieldLabel}>
                {t.product.size}
                {selectedSize ? `: ${selectedSize}` : ''}
              </span>
              {measurementRows.length > 0 ? (
                <button
                  type="button"
                  className={styles.sizeGuideLink}
                  onClick={openMeasurements}
                  aria-controls={MEASUREMENTS_ACCORDION_ID}
                >
                  {t.product.sizeGuide}
                </button>
              ) : null}
            </div>
            <div className={styles.tileGrid}>
              {availableSizes.map((s) => {
                const disabled = isSizeUnavailable(s);
                const selected = s === selectedSize;
                const cls = [
                  styles.tile,
                  selected ? styles.tileSelected : '',
                  disabled ? styles.tileDisabled : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={s}
                    type="button"
                    className={cls}
                    aria-pressed={selected}
                    aria-disabled={disabled || undefined}
                    tabIndex={disabled ? -1 : 0}
                    onClick={disabled ? undefined : () => handleSelectSize(s)}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedVariant ? (
            selectedVariant.stock > 0 ? (
              <p className={isLowStock ? styles.lowStock : styles.inStock}>
                {isLowStock ? t.product.lowStock : t.product.inStock}
              </p>
            ) : (
              <p className={styles.outOfStock}>{t.product.outOfStock}</p>
            )
          ) : showSelectHint ? (
            <p className={styles.hint}>{t.product.selectVariant}</p>
          ) : null}

          <div className={styles.cartRow}>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              disabled={addDisabled}
              onClick={handleAdd}
            >
              {t.product.addToCart}
            </Button>
          </div>

          <div className={styles.accordions}>
            <Accordion title={t.product.descriptionLabel} defaultOpen>
              {product.description ?? '—'}
            </Accordion>
            <Accordion title={t.product.detailsLabel}>
              <DetailsBlock product={product} />
            </Accordion>
            {measurementRows.length > 0 ? (
              <Accordion id={MEASUREMENTS_ACCORDION_ID} title={t.product.measurementsLabel}>
                <MeasurementsBlock
                  rows={measurementRows}
                  selectedSize={selectedSize}
                  note={product.measurementsNote}
                />
              </Accordion>
            ) : null}
            <Accordion title={t.product.shippingReturnsLabel}>
              {t.product.shippingReturnsBody}{' '}
              <Link to="/entregas-e-devolucoes">{t.product.shippingReturnsLink}</Link>.
            </Accordion>
          </div>
        </div>
      </div>

      {related.length > 0 ? (
        <section className={`container ${styles.related}`}>
          <div className={styles.relatedHeader}>
            <p className="script-accent">{t.product.relatedScript}</p>
          </div>
          <div className={styles.relatedScroll}>
            {related.map((p) => (
              <div key={p.id} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {lightboxIndex !== null ? (
        <Lightbox
          images={product.images}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}
    </>
  );
}
